import { Component, OnInit } from '@angular/core';
import { PrimaryData, ReferenceLibrary, LookupValues, StateObj, Location, ImagilityBaseResponse, File, TaskSummaryOverview } from 'app-models';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as moment from 'moment';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { EMPTY, Observable, Subject, combineLatest, timer } from 'rxjs';
import { first, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';
import { UpdateDeterminationService } from './update-determination.service';
import { Store } from '@ngrx/store';
import { VisaExtendedState } from '../../utility/interfaces/visa-extended-state';
import { DirtyFormGuard, MarkCleanFormAction, MarkDirtyFormAction } from 'dirty-check-store';
import { ReferenceLibraryService } from 'reference-library';
import { loadSubTaskDetails, updateStepStatusToInitialState } from 'visa-store';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { PetitionerService } from 'src/app/services/petitioner.service';
import * as Task from './../../../superTask/task-state/actions/task.actions';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from 'src/app/modules/shared/modal/confirmation-modal/confirmation-modal.component';
import { CustomErrorToastComponent } from 'custom-error-toast';

@Component({
  selector: 'app-update-determination',
  templateUrl: './update-determination.component.html',
  styleUrls: ['./update-determination.component.scss']
})
export class UpdateDeterminationComponent implements OnInit {
  primaryData: PrimaryData;
  toYear: string;
  fromYear: string;
  updateDeterminationForm: FormGroup;
  observableSubscription$ = new Subject();
  gcPwdCaseId: number;
  superTaskId: number;
  initialDecisionList: LookupValues = {
    code: null,
    createdBy: null,
    description: null,
    group: null,
    id: null,
    modifiedBy: null,
    name: 'Select',
    refGroupdesc: null
  };
  initialRefLibrary: ReferenceLibrary = {
    code: null,
    desc: null,
    group: null,
    id: null,
    name: 'Select'
  };
  decisionList: LookupValues[] = [];
  wageUnitList: ReferenceLibrary[] = [];
  OESWageLevelList: ReferenceLibrary[] = [];
  prevailingWageSourceList: ReferenceLibrary[] = [];
  workLocationList: Location[] = [];
  resetForm: {
    gcPwdCaseId: number;
    pwtrackNumber: string;
    decisionCode: string;
    filingDate: Date;
    determinationDate: Date;
    validityFromDate: Date;
    validityToDate: Date;
    socCode: string;
    socTitle: string;
    determinationWageInfo: {
      locationAddrId: number;
      locationAddrName: string;
      prvlWageAmt: number;
      prvlWageUnitCode: string;
      oesWageLevelCode: string;
      prvlWageSourceCode: string;
      otherWageSource: string;
      notes: string;
    }[]
  };
  fileUploaded: File[] = [];
  private _groups: FormControl[];
  minValidityToDate: Date;

  constructor(
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private updateDeterminationService: UpdateDeterminationService,
    private store: Store<VisaExtendedState>,
    private referenceLibraryService: ReferenceLibraryService,
    private toastrService: ToastrService,
    private guard: DirtyFormGuard,
    private petitionerService: PetitionerService,
    public dialog: MatDialog,
  ) {
    this.primaryData = this.dynamicDialogConfig.data;
    this.gcPwdCaseId = this.primaryData.caseId;
    this.superTaskId = this.primaryData.superTaskId;
    this.toYear = moment().add(5, 'years').format('YYYY');
    this.fromYear = moment().subtract(5, 'years').format('YYYY');
  }

  ngOnInit() {
    this.updateDeterminationForm = new FormGroup({
      gcPwdCaseId: new FormControl(null),
      pwtrackNumber: new FormControl(null, Validators.required),
      decisionCode: new FormControl(null, Validators.required),
      filingDate: new FormControl(null, Validators.required),
      determinationDate: new FormControl(null),
      validityFromDate: new FormControl(null),
      validityToDate: new FormControl(null),
      socCode: new FormControl(null, [], this.validateSOCCode.bind(this)),
      socTitle: new FormControl(null),
      determinationWageInfo: new FormArray([])
    }, [this.determinationDateRequired, this.validityFromDateRequired, this.validityToDateRequired]);

    this.updateDeterminationForm.statusChanges.pipe(
      map(() => this.updateDeterminationForm.dirty),
    ).pipe(takeUntil(this.observableSubscription$)).subscribe(dirty => {
      if (dirty) {
        this.store.dispatch(new MarkDirtyFormAction({
          dirty: true
        }));
      } else {
        this.store.dispatch(new MarkCleanFormAction({
          dirty: false
        }));
      }
    });

    combineLatest([
      this.updateDeterminationService.getDecisionList('PWDEFILE'),
      this.referenceLibraryService.getReferenceData('PAYTYP'),
      this.referenceLibraryService.getReferenceData('PRVLWGLVL'),
      this.referenceLibraryService.getReferenceData('PRVLWAGESRC'),
    ]).pipe(take(1))
      .subscribe(([decisionList, wageUnitList, OESWageLevelList, prevailingWageSourceList]) => {
        (decisionList as LookupValues[]).unshift(this.initialDecisionList);
        (wageUnitList as ReferenceLibrary[]).unshift(this.initialRefLibrary);
        (OESWageLevelList as ReferenceLibrary[]).unshift(this.initialRefLibrary);
        (prevailingWageSourceList as ReferenceLibrary[]).unshift(this.initialRefLibrary);
        this.decisionList = decisionList;
        this.wageUnitList = wageUnitList;
        this.OESWageLevelList = OESWageLevelList;
        this.prevailingWageSourceList = prevailingWageSourceList;
        this.getWorkLocation();
      });
  }

  determinationDateRequired(group: FormGroup): { [s: string]: boolean } {
    if (group.value.decisionCode && group.value.decisionCode === 'PWDISSUED' &&
      !group.value.determinationDate) {
      return { determinationDateIsRequired: true };
    }
    return null;
  }

  validityFromDateRequired(group: FormGroup): { [s: string]: boolean } {
    if (group.value.decisionCode && group.value.decisionCode === 'PWDISSUED' &&
      !group.value.validityFromDate) {
      return { validityFromDateIsRequired: true };
    }
    return null;
  }

  validityToDateRequired(group: FormGroup): { [s: string]: boolean } {
    if (group.value.decisionCode && group.value.decisionCode === 'PWDISSUED' &&
      !group.value.validityToDate) {
      return { validityToDateIsRequired: true };
    }
    return null;
  }

  otherWageSourceRequired(group: FormGroup): { [s: string]: boolean } {
    if (group.value.prvlWageSourceCode === 'OTHER' &&
      (!group.value.otherWageSource || (group.value.otherWageSource && group.value.otherWageSource.trim() === ''))) {
      return { otherWageSourceIsRequired: true };
    }
    return null;
  }

  prvlWageAmtRequired(group: FormGroup): { [s: string]: boolean } {
    if (this.updateDeterminationForm.value.decisionCode &&
      this.updateDeterminationForm.value.decisionCode === 'PWDISSUED' && !group.value.prvlWageAmt) {
      return { prvlWageAmtIsRequired: true };
    }
    return null;
  }

  prvlWageUnitCodeRequired(group: FormGroup): { [s: string]: boolean } {
    if (this.updateDeterminationForm.value.decisionCode &&
      this.updateDeterminationForm.value.decisionCode === 'PWDISSUED' && !group.value.prvlWageUnitCode) {
      return { prvlWageUnitCodeIsRequired: true };
    }
    return null;
  }

  oesWageLevelCodeRequired(group: FormGroup): { [s: string]: boolean } {
    if (this.updateDeterminationForm.value.decisionCode &&
      this.updateDeterminationForm.value.decisionCode === 'PWDISSUED' && !group.value.oesWageLevelCode) {
      return { oesWageLevelCodeIsRequired: true };
    }
    return null;
  }

  prvlWageSourceCodeRequired(group: FormGroup): { [s: string]: boolean } {
    if (this.updateDeterminationForm.value.decisionCode &&
      this.updateDeterminationForm.value.decisionCode === 'PWDISSUED' && !group.value.prvlWageSourceCode) {
      return { prvlWageSourceCodeIsRequired: true };
    }
    return null;
  }

  validateSOCCode(control: FormControl): Promise<any> | Observable<any> {
    if (this.updateDeterminationForm && this.updateDeterminationForm.value.decisionCode &&
      this.updateDeterminationForm.value.decisionCode === 'PWDISSUED' && control.value) {
      // this.ngxUiLoaderService.stopAll();
      return timer(0).pipe(
        take(1),
        switchMap(() => {
          // this.ngxUiLoaderService.stopAll();
          return this.updateDeterminationService.ongetSOCCode(control.value);
        }),
        map(data => {
          // this.ngxUiLoaderService.stopAll();
          if (data) {
            (control.parent as FormGroup).patchValue({
              socTitle: data.title
            });
            return null;
          } else {
            (control.parent as FormGroup).patchValue({
              socTitle: null
            });
            return { SOCCodeInvalid: true };
          }
        })
      );
    } else {
      // return new Promise<any>((resolve, reject) => {
      //   resolve(null);
      // });
      return timer(0).pipe(take(1), map(() => null));
    }
  }

  getWorkLocation() {
    this.updateDeterminationService.onPostWorkLocations(this.gcPwdCaseId).pipe(
      take(1),
      mergeMap((response) => {
        if (response) {
          return this.updateDeterminationService.onGetWorkLocations(this.gcPwdCaseId);

        } else {
          return EMPTY;
        }
      })
    )
      .pipe(take(1))
      .subscribe((workLocationList: Location[]) => {
        const workLocationListTemp: Location[] = [];
        if (workLocationList.length > 0) {
          workLocationList.forEach((workLocation: Location) => {
            this.referenceLibraryService.getStatesFromStore(workLocation.address.countryCode)
              .pipe(take(1))
              .subscribe((stateList: StateObj[]) => {
                if (stateList) {
                  this.postStateListFetch(stateList, workLocation, workLocationListTemp, workLocationList);
                } else {
                  this.referenceLibraryService.getStatesFromApi(workLocation.address.countryCode)
                    .pipe(take(1))
                    .subscribe((stateList: StateObj[]) => {
                      this.postStateListFetch(stateList || [], workLocation, workLocationListTemp, workLocationList);
                    });
                }
              });
          });
        } else {
          this.getUpdateDeterminationDetails();
        }
        this.getFileUploaded();
      });
  }

  getFileUploaded() {
    this.updateDeterminationService.onGetPrewailingWageUploadedFile(this.gcPwdCaseId)
      .pipe(take(1))
      .subscribe((response: ImagilityBaseResponse) => {
        if (response.status === 200) {
          this.fileUploaded.push(response.data);
        } else {
          this.fileUploaded = [];
        }
      });
  }

  onViewFile() {
    window.open(environment.docs + this.fileUploaded[0].fileLocation.replace('/var/imagility/uploads/', ''), '_blank');
  }

  onDownloadFile() {
    this.updateDeterminationService.onDownload(environment.docs + this.fileUploaded[0].fileLocation.replace('/var/imagility/uploads/', ''), `${this.fileUploaded[0].fileName}.${this.fileUploaded[0].fileLocation.substr(this.fileUploaded[0].fileLocation.lastIndexOf('.') + 1)}`);
  }

  dialogueInitializer(title: string, message: string) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: "300px",
      data: {
        update: { title, message },
      },
    });
    return dialogRef;
  }

  onDeleteFile() {
    const title = 'Confirm Deletion!';
    const message = 'Do you want to delete this record?';
    this.dialogueInitializer(title, message).afterClosed().subscribe(result => {
      if (result) {
        this.updateDeterminationService.onDelete(this.gcPwdCaseId, this.fileUploaded[0].id)
          .pipe(take(1))
          .subscribe((response: ImagilityBaseResponse) => {
            if (response.status === 200) {
              this.toastrService.success(response.message, 'Success');
            } else {
              CustomErrorToastComponent.showErrorToast(this.toastrService, response.message);
            }
            this.getFileUploaded();
          });
      }
    });
  }

  myUploader(event, fileUpload) {
    const file: any = event.files[0];
    file.fileName = event.files[0].name;
    const payload = new FormData();
    payload.append('file', file, file.name);
    this.updateDeterminationService.onPrewailingWageFileUpload(this.gcPwdCaseId, payload).pipe(
      take(1)
    ).subscribe((response: ImagilityBaseResponse) => {
      if (response.status === 200) {
        this.toastrService.success(response.message, 'Success');
      } else {
        CustomErrorToastComponent.showErrorToast(this.toastrService, response.message);
      }
      fileUpload.clear();
      this.getFileUploaded();
    });
  }

  postStateListFetch(stateList: StateObj[], workLocation: Location, workLocationListTemp: Location[], workLocationList: Location[]) {
    let stateName = workLocation.address.stateProvinceName;
    if (stateList && stateList.length > 0) {
      const stateObj = stateList.find(state => state.stateProvinceCode === workLocation.address.stateProvinceCode);
      if (stateObj) {
        stateName = stateObj.stateProvinceName;
      }
    }
    workLocationListTemp.push({
      ...workLocation,
      address: {
        ...workLocation.address,
        stateProvinceName: stateName
      },
      locationOptionLabel: workLocation.address ?
        `${workLocation.address.addressLine1}, ${workLocation.address.city}, ${stateName}` : ''
    });
    if (workLocationListTemp.length === workLocationList.length) {
      // workLocationListTemp.unshift(this.initialWorkLocation);
      this.workLocationList = JSON.parse(JSON.stringify(workLocationListTemp));
      this.getUpdateDeterminationDetails();
    }
  }

  onValidityFromDate() {
    if (this.updateDeterminationForm.get('validityFromDate').value) {
      const minDate = new Date(this.updateDeterminationForm.get('validityFromDate').value);
      minDate.setDate(minDate.getDate() + 1);
      this.minValidityToDate = new Date(minDate);
    } else {
      this.minValidityToDate = null;
    }
  }

  getUpdateDeterminationDetails() {
    this.updateDeterminationService.onGetUpdateDetermination(this.gcPwdCaseId)
      .pipe(take(1))
      .subscribe((updateDetermination: ImagilityBaseResponse) => {
        if (updateDetermination.status === 200 && updateDetermination.data) {
          this.resetForm = {
            gcPwdCaseId: this.gcPwdCaseId,
            pwtrackNumber: updateDetermination.data.pwtrackNumber,
            decisionCode: updateDetermination.data.decisionCode && String(updateDetermination.data.decisionCode).trim() !== '' ?
              updateDetermination.data.decisionCode : null,
            filingDate: updateDetermination.data.filingDate ? new Date(updateDetermination.data.filingDate) : null,
            determinationDate: updateDetermination.data.determinationDate ? new Date(updateDetermination.data.determinationDate) : null,
            validityFromDate: updateDetermination.data.validityFromDate ? new Date(updateDetermination.data.validityFromDate) : null,
            validityToDate: updateDetermination.data.validityToDate ? new Date(updateDetermination.data.validityToDate) : null,
            socCode: updateDetermination.data.socCode,
            socTitle: updateDetermination.data.socTitle,
            determinationWageInfo: []
          };
          this.workLocationList.forEach(x => {
            const wageInfo = updateDetermination.data.determinationWageInfo.find(y => y.locationAddrId === x.id);
            if (wageInfo) {
              this.resetForm.determinationWageInfo.push({
                locationAddrId: x.id,
                locationAddrName: x.locationOptionLabel,
                prvlWageAmt: wageInfo.prvlWageAmt,
                prvlWageUnitCode: wageInfo.prvlWageUnitCode && String(wageInfo.prvlWageUnitCode).trim() !== '' ?
                  wageInfo.prvlWageUnitCode : null,
                oesWageLevelCode: wageInfo.oesWageLevelCode && String(wageInfo.oesWageLevelCode).trim() !== '' ?
                  wageInfo.oesWageLevelCode : null,
                prvlWageSourceCode: wageInfo.prvlWageSourceCode && String(wageInfo.prvlWageSourceCode).trim() !== '' ?
                  wageInfo.prvlWageSourceCode : null,
                otherWageSource: wageInfo.otherWageSource,
                notes: wageInfo.notes
              });
            } else {
              this.resetForm.determinationWageInfo.push({
                locationAddrId: x.id,
                locationAddrName: x.locationOptionLabel,
                prvlWageAmt: null,
                prvlWageUnitCode: null,
                oesWageLevelCode: null,
                prvlWageSourceCode: null,
                otherWageSource: null,
                notes: null
              });
            }
          });
        } else {
          this.resetForm = {
            gcPwdCaseId: this.gcPwdCaseId,
            pwtrackNumber: null,
            decisionCode: null,
            filingDate: null,
            determinationDate: null,
            validityFromDate: null,
            validityToDate: null,
            socCode: null,
            socTitle: null,
            determinationWageInfo: []
          };
          this.workLocationList.forEach(x => {
            this.resetForm.determinationWageInfo.push({
              locationAddrId: x.id,
              locationAddrName: x.locationOptionLabel,
              prvlWageAmt: null,
              prvlWageUnitCode: null,
              oesWageLevelCode: null,
              prvlWageSourceCode: null,
              otherWageSource: null,
              notes: null
            });
          });
        }
        const determinationWageInfoFormGroup = this.updateDeterminationForm.get('determinationWageInfo') as FormArray;
        determinationWageInfoFormGroup.clear();
        this.workLocationList.forEach(x => {
          determinationWageInfoFormGroup.push(new FormGroup({
            locationAddrId: new FormControl(null),
            locationAddrName: new FormControl(null),
            prvlWageAmt: new FormControl(null),
            prvlWageUnitCode: new FormControl(null),
            oesWageLevelCode: new FormControl(null),
            prvlWageSourceCode: new FormControl(null),
            otherWageSource: new FormControl(null),
            notes: new FormControl(null)
          }, [this.otherWageSourceRequired.bind(this), this.prvlWageAmtRequired.bind(this),
          this.prvlWageUnitCodeRequired.bind(this), this.oesWageLevelCodeRequired.bind(this),
          this.prvlWageSourceCodeRequired.bind(this)]));
        });
        this.onReset();
      });
  }

  onReset() {
    const determinationWageInfo = this.resetForm.determinationWageInfo.map(x => {
      return {
        ...x
      };
    });
    this.updateDeterminationForm.reset({
      ...this.resetForm,
      determinationWageInfo
    });
    this.onValidityFromDate();
    // window.location.reload();
  }

  onDecisionChange() {
    const determinationWageInfo = this.workLocationList.map(x => {
      return {
        locationAddrId: x.id,
        locationAddrName: x.locationOptionLabel,
        prvlWageAmt: null,
        prvlWageUnitCode: null,
        oesWageLevelCode: null,
        prvlWageSourceCode: null,
        otherWageSource: null,
        notes: null
      };
    });
    this.updateDeterminationForm.patchValue({
      determinationDate: null,
      validityFromDate: null,
      validityToDate: null,
      socCode: null,
      socTitle: null,
      determinationWageInfo
    });
    this.onValidityFromDate();
  }

  get groups() {
    if (!this._groups) {
      this._groups = (this.updateDeterminationForm.get('determinationWageInfo') as FormArray).controls as FormControl[];
    }
    return this._groups;
  }

  handleClose() {
    this.guard.canDeactivate().pipe(first()).subscribe((response: boolean) => {
      if (response) {
        this.store.dispatch(new MarkCleanFormAction({
          dirty: false
        }));
        this.dynamicDialogRef.close();
      }
      else {
        return false;
      }
    });
    window.location.reload();
  }

  onSubmit() {
    this.updateDeterminationService.onGCPWDUpdateDeterminationSave(this.gcPwdCaseId, {
      ...this.updateDeterminationForm.value,
      determinationDate: this.updateDeterminationForm.value.determinationDate ?
        moment(this.updateDeterminationForm.value.determinationDate).format('YYYY-MM-DD') : null,
      filingDate: this.updateDeterminationForm.value.filingDate ?
        moment(this.updateDeterminationForm.value.filingDate).format('YYYY-MM-DD') : null,
      validityFromDate: this.updateDeterminationForm.value.validityFromDate ?
        moment(this.updateDeterminationForm.value.validityFromDate).format('YYYY-MM-DD') : null,
      validityToDate: this.updateDeterminationForm.value.validityToDate ?
        moment(this.updateDeterminationForm.value.validityToDate).format('YYYY-MM-DD') : null
    }).pipe(
      take(1),
      mergeMap((response: ImagilityBaseResponse) => {
        if (response.status === 200) {
          this.toastrService.success(response.message, 'Success');
        } else {
          CustomErrorToastComponent.showErrorToast(this.toastrService, response.message);
        }
        this.getUpdateDeterminationDetails();
        return this.petitionerService.getSuperTask(this.superTaskId);
      })
    ).subscribe((taskSummaryOverview: TaskSummaryOverview) => {
      this.store.dispatch(new Task.SetTaskSummaryOverview(taskSummaryOverview));
      this.store.dispatch(loadSubTaskDetails({
        taskId: parseInt(this.dynamicDialogConfig.data.subTaskId.toString()),
        visaTypeCode: this.dynamicDialogConfig.data.getTaskType
      }));
     
    });
  }

  ngOnDestroy() {
    this.observableSubscription$.next();
    this.observableSubscription$.complete();
    // Update the Step Status state to empty state
    this.store.dispatch(updateStepStatusToInitialState());
  }

}
